import type { ReactNode } from 'react';
import { Dialog } from '@dataroom/shared';

interface ModalProps {
  /** Accessible dialog title, rendered as the heading and wired to aria-labelledby. */
  title: string;
  /** Optional sub-line under the title. */
  description?: string;
  onClose: () => void;
  children: ReactNode;
  /** Footer content (actions), pinned to the bottom border. */
  footer: ReactNode;
  /** `lg` widens the dialog for tabular content (e.g. upload problems). */
  size?: 'md' | 'lg';
  /** Extra classes for the scrolling body (e.g. a layout that fills it). */
  bodyClassName?: string;
  /** Beside the title, at the right of the header (e.g. a help "?"). */
  headerAside?: ReactNode;
}

/**
 * The admin's modal shell — now the shared DataRoom UI Dialog: role="dialog",
 * aria-modal, labelled heading + description, focus moved in on open and
 * trapped while open, focus returned to the opener on close, Escape (topmost
 * dialog only) and backdrop click to close, bottom sheet below 640px.
 * Presentational — the page owns open state and the actions in `footer`.
 */
export function Modal({
  title,
  description,
  onClose,
  children,
  footer,
  size = 'md',
  bodyClassName,
  headerAside,
}: ModalProps) {
  return (
    <Dialog
      title={title}
      description={description}
      onClose={onClose}
      footer={footer}
      size={size === 'lg' ? 'lg' : 'md'}
      bodyClassName={bodyClassName}
      headerAside={headerAside}
    >
      {children}
    </Dialog>
  );
}
